import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Single nav item — `to` is a router path, `Icon` renders inside the
 * sidebar menu button. The full nav-config (sections + items, with
 * permission gating) is assembled in `nav-config.ts` and consumed by
 * `AppSidebar`; this type is its single-item leaf.
 */
export interface NavItem {
  to: string;
  label: string;
  Icon?: LucideIcon;
  /**
   * Match strategy. `exact` (default) requires `pathname === to`; `prefix`
   * matches when `pathname === to` OR `pathname.startsWith(to + '/')` —
   * used for nested-route sections like `/admin/*`.
   */
  match?: 'exact' | 'prefix';
  /**
   * When true, render a plain anchor (full browser navigation) instead of
   * a React Router `<Link>`. Required for paths served outside the SPA
   * route table — e.g. `/docs/*`, which is served by Astro/Starlight in
   * dev (via the Vite proxy) and by SWA in prod. Without this, the
   * router intercepts the click and falls through to the 404 catch-all.
   */
  external?: boolean;
  /** Optional attention badge (e.g. hub queue count). Hidden when count is 0. */
  badge?: { count: number; ariaLabel: string };
}

interface NavGroupProps {
  label?: string;
  items: ReadonlyArray<NavItem>;
  /** Optional trailing content beneath the items (used by Admin sub-tree). */
  children?: ReactNode;
}

function isActive(pathname: string, item: NavItem): boolean {
  if (item.match === 'prefix') {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  return pathname === item.to;
}

/**
 * Sidebar section: an optional label + a list of nav items. Each item is
 * a `<Link>` (so navigation is client-side) wrapped in a `SidebarMenuButton`
 * for the shadcn-styled press surface, and tooltip-ready when collapsed.
 */
export function NavGroup({ label, items, children }: NavGroupProps) {
  const { pathname } = useLocation();
  return (
    <SidebarGroup>
      {label !== undefined ? <SidebarGroupLabel>{label}</SidebarGroupLabel> : null}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.to}>
              <SidebarMenuButton asChild isActive={isActive(pathname, item)} tooltip={item.label}>
                {item.external === true ? (
                  <a href={item.to}>
                    {item.Icon !== undefined ? (
                      <item.Icon className="h-4 w-4" aria-hidden="true" />
                    ) : null}
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <Link to={item.to}>
                    {item.Icon !== undefined ? (
                      <item.Icon className="h-4 w-4" aria-hidden="true" />
                    ) : null}
                    <span>{item.label}</span>
                  </Link>
                )}
              </SidebarMenuButton>
              {item.badge !== undefined && item.badge.count > 0 ? (
                <SidebarMenuBadge>
                  <span aria-hidden="true">{item.badge.count}</span>
                  <span className="sr-only">{item.badge.ariaLabel}</span>
                </SidebarMenuBadge>
              ) : null}
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
        {children}
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
